// Import React polyfill FIRST to ensure React is globally available
import "./react-polyfill";

// src/main.tsx
import { createRoot } from "react-dom/client";
import React from "react";

// Comprehensive React availability check
if (typeof window !== "undefined") {
  (window as any).React = React;
  // Ensure Children API is specifically available
  if (!React.Children) {
    console.error("React.Children is not available!");
  }
}

import "./index.css";
import "swiper/swiper-bundle.css";
import "flatpickr/dist/flatpickr.css";
import 'sweetalert2/dist/sweetalert2.min.css';
import './styles/sweetalert-fix.css';

import "./styles/globals.css";
// import "./styles/layout.css";

import App from "./App.tsx";
import { AppWrapper } from "./components/common/PageMeta.tsx";
import { ThemeProvider } from "./context/ThemeContext.tsx";

// Stray errors from event handlers, promises or third-party scripts are only logged: the app can
// usually keep working after them, so they must not replace the whole page.
if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => console.error("Global error:", e?.error ?? e));
  window.addEventListener("unhandledrejection", (e) => console.error("Unhandled rejection:", e?.reason ?? e));
}

// Shows the error screen only when React itself fails to render, which would otherwise leave a blank page.
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { err: any }> {
  state = { err: null as any };

  static getDerivedStateFromError(err: any) {
    return { err };
  }

  componentDidCatch(err: any, info: React.ErrorInfo) {
    console.error("Render error:", err, info?.componentStack);
  }

  render() {
    const { err } = this.state;
    if (!err) return this.props.children;
    return (
      <div style={{ padding: 20 }}>
        <h2 style={{ color: "red" }}>App error</h2>
        <pre>{String(err?.message ?? err)}</pre>
        <details style={{ whiteSpace: "pre-wrap" }}>
          {String(err?.stack ?? "")}
        </details>
      </div>
    );
  }
}

// Wait for DOM to be ready before initializing React app
function initializeApp() {
  createRoot(document.getElementById("root")!).render(
    <ThemeProvider>
      <AppWrapper>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </AppWrapper>
    </ThemeProvider>,
  );

  // Mark boot complete for white-screen diagnostics
}

// Initialize app when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeApp);
} else {
  initializeApp();
}
